import { Injectable } from '@angular/core';
import { FeatureState } from '@modeso/aumico-lib-core-fe';
import { UntilDestroy } from '@ngneat/until-destroy';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import * as fromSurveysAction from '../actions/surveys.actions';
import { SurveySummary } from '../interfaces/survey-summary.interface';
import { Survey } from '../interfaces/survey.interface';
import { UserSurveyStatus, UserSurveyStatusDto } from '../interfaces/user-survey-status.interface';
import * as fromFeature from '../reducers/surveys.reducer';
import { ISurveysProvider } from './surveys.provider.interface';
import { ISurveyResponses } from '../interfaces/states/surveys-feature-state.interface';

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class SurveysProvider implements ISurveysProvider {
    constructor(private store: Store<FeatureState>) {}

    public dispatchCreateSurvey(): void {
        this.store.dispatch(fromSurveysAction.createSurvey.createSurvey());
    }

    public dispatchDeleteSurvey(id: string): void {
        this.store.dispatch(fromSurveysAction.deleteSurvey.deleteSurvey({ payload: id }));
    }

    public dispatchStopSurvey(id: string): void {
        this.store.dispatch(fromSurveysAction.stopSurvey.stopSurvey({ payload: id }));
    }

    public dispatchDownloadSurveyCsv(id: string): void {
        this.store.dispatch(fromSurveysAction.downloadSurveyCsv.downloadSurveyCsv({ payload: id }));
    }

    public dispatchGetActiveSurvey(): void {
        this.store.dispatch(fromSurveysAction.getActiveSurvey.getActiveSurvey());
    }

    public dispatchGetSurveyById(id: string): void {
        this.store.dispatch(fromSurveysAction.getSurveyById.getSurveyById({ payload: id }));
    }

    public dispatchGetEligibleUsersSurveyStatus(id: string): void {
        this.store.dispatch(
            fromSurveysAction.getEligibleUsersSurveyStatus.getEligibleUsersSurveyStatus({ payload: id }),
        );
    }

    public getEligibleUsersSurveyStatus$(): Observable<UserSurveyStatus[]> {
        return this.store.pipe(select(fromFeature.selectEligibleUsersSurveyStatus));
    }

    public getActiveSurvey$(): Observable<Survey> {
        return this.store.pipe(select(fromFeature.selectActiveSurvey));
    }

    public getIsVisible$(): Observable<boolean> {
        return this.store.pipe(select(fromFeature.selectIsVisible));
    }

    public getCurrentQuestion$(): Observable<number> {
        return this.store.pipe(select(fromFeature.selectCurrentQuestion));
    }

    public getCurrentAnswers$(): Observable<ISurveyResponses> {
        return this.store.pipe(select(fromFeature.selectCurrentAnswers));
    }

    public checkUserEligibility$(id: string): void {
        this.store.dispatch(fromSurveysAction.checkUserEligibility.checkUserEligibility({ payload: id }));
    }

    public dispatchSaveResponse(surveyId: string, questionNumber: number, response: string): void {
        this.store.dispatch(fromSurveysAction.saveResponse.saveResponse({ surveyId, questionNumber, response }));
    }

    public startSurvey$(id: string): void {
        this.store.dispatch(fromSurveysAction.startSurvey.startSurvey({ payload: id }));
    }

    public dismissSurvey$(id: string): void {
        this.store.dispatch(fromSurveysAction.dismissSurvey.dismissSurvey({ payload: id }));
    }

    public completeSurvey$(id: string): void {
        this.store.dispatch(fromSurveysAction.completeSurvey.completeSurvey({ payload: id }));
    }

    public dispatchGetUserSurveyStatus$(id: string): void {
        this.store.dispatch(fromSurveysAction.getUserSurveyStatus.getUserSurveyStatus({ payload: id }));
    }

    public getUserSurveyStatus$(): Observable<UserSurveyStatusDto> {
        return this.store.pipe(select(fromFeature.selectUserSurveyStatus));
    }

    public showSurveyPopup(): void {
        return this.store.dispatch(fromSurveysAction.showSurveyPopup.showSurveyPopup());
    }

    public nextQuestion(): void {
        this.store.dispatch(fromSurveysAction.nextQuestion.nextQuestion());
    }

    public previousQuestion(): void {
        this.store.dispatch(fromSurveysAction.previousQuestion.previousQuestion());
    }

    public dispatchGetSurveyResponses$(id: string): void {
        return this.store.dispatch(fromSurveysAction.getSurveyResponses.getSurveyResponses({ payload: id }));
    }

    public getSurveyResponses$(id: string): void {
        return this.store.dispatch(fromSurveysAction.getSurveyResponses.getSurveyResponses({ payload: id }));
    }

    public getOpenedSurvey$(): Observable<Survey> {
        return this.store.pipe(select(fromFeature.selectOpenedSurvey));
    }

    public dispatchGetAllSurveys(): void {
        return this.store.dispatch(fromSurveysAction.getAllSurveys.getAllSurveys());
    }

    public getAllSurveys(): Observable<SurveySummary[]> {
        return this.store.pipe(select(fromFeature.selectSurveysList));
    }

    public dispatchGetUsersToNotify$(id: string): void {
        return this.store.dispatch(fromSurveysAction.getUsersToNotify.getUsersToNotify({ payload: id }));
    }

    public dispatchMarkUserAsNotified$(id: string): void {
        return this.store.dispatch(fromSurveysAction.markUserAsNotified.markUserAsNotified({ payload: id }));
    }

    public resetCurrentQuestion(): void {
        this.store.dispatch(fromSurveysAction.resetQuestionNumber());
    }

    public dispatchOpenedSurveySummary(summary: SurveySummary): void {
        this.store.dispatch(fromSurveysAction.getOpenedSurveySummary({ payload: summary }));
    }

    public getOpenedSurveySummary$(): Observable<SurveySummary> {
        return this.store.pipe(select(fromFeature.selectOpenedSurveySummary));
    }
}
